/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useEffect, useRef, useState, useCallback } from 'react';

interface Notification {
  id: string;
  type: string;
  category: string;
  title: string;
  message: string;
  link?: string;
  is_read: boolean;
  created_at: string;
  timestamp?: string;
}

interface WebSocketMessage {
  type: string;
  data: Notification;
}

const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL || 'https://internal.podstack.ai/auth';

// Convert HTTP URL to WebSocket URL
const getWebSocketUrl = () => {
  let baseUrl = AUTH_API_URL.replace(/\/$/, ''); // Remove trailing slash
  const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';

  // Check if base URL already includes /api/v1 and remove it to avoid duplication
  if (baseUrl.endsWith('/api/v1')) {
    baseUrl = baseUrl.slice(0, -7); // Remove '/api/v1'
  }

  const wsUrl = baseUrl.replace(/^https?/, wsProtocol);
  return `${wsUrl}/api/v1/notifications/ws`;
};

export const useNotificationWebSocket = (
  onNotification: (notification: Notification) => void
) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // 1 second

  // Keep latest callback in a ref so connect() doesn't change when parent re-renders.
  // This prevents useEffect from disconnecting and reconnecting (closing the socket before it's established).
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const connect = useCallback(() => {
    // Get token from localStorage
    let token: string | null;
    try {
      token = localStorage.getItem('token');
    } catch {
      token = null;
    }
    if (!token) {
      setError('No authentication token');
      return;
    }

    // Close existing connection if any. Detach handlers first so the old
    // socket's onclose doesn't schedule a zombie reconnect, and close with an
    // explicit code - close() without a code makes the browser report 1005.
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close(1000, 'replaced');
      wsRef.current = null;
    }

    try {
      // Include token as query parameter for WebSocket auth
      const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          if (message.type === 'notification' && message.data) {
            onNotificationRef.current(message.data);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else {
          setError('Max reconnection attempts reached');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Error creating WebSocket:', err);
      setError('Failed to create WebSocket connection');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      // Intentional close (unmount/navigation): detach onclose so no
      // reconnect gets scheduled for a socket nobody owns anymore - this was
      // leaking parallel connections - and close with an explicit code
      // instead of the code-less close that surfaced as 1005 in the console.
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close(1000, 'unmount');
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // This client has no ping/heartbeat, so a dropped connection is normally
  // only noticed via `onclose`. Safari suspends/throttles backgrounded tabs
  // aggressively enough that `onclose` can be delayed well past when the
  // connection actually died - re-verify (and reconnect if needed) the
  // moment the tab regains visibility.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
      connect();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connect]);

  return {
    isConnected,
    error,
    reconnect: connect,
    disconnect,
  };
};
